import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  ChartOptions,
  Chart,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { FiFlag } from "react-icons/fi";
import styles from "./VaultAreaChart.module.css";
import { TChartData, TMarkerPoint } from "@components/Charts/configs";

const COLORS = {
  primary: "#00B7A8",
  primaryLight: "#66D9CC",
  textPrimary: "#1A1A1A",
  textMuted: "#4A4A4A",
  textSecondary: "#4A4A4A",
  white: "#FFFFFF",
  borderLight: "#E0E0E0",
  divider: "#E0E0E0",
  shadow: "0 4px 16px rgba(0,0,0,0.10)",
  primaryHoverRGBA: "rgba(0, 183, 168, 0.8)",
  gridLine: "#E0E0E0",
  markerBg: "#FFFFFF",
  markerBorder: "#E0E0E0",
} as const;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend
);

type TVaultAreaChartProps = {
  chartData: TChartData & {
    hideLegend?: boolean;
    showExchangeRate?: boolean;
    selectedTimeframeApr?: number | null;
    aprLabel?: string;
    rawData?: Array<{
      date: string;
      exchangeRate: number;
      monthlyChange?: number;
    }>;
  };
  markerPoints: TMarkerPoint[];
  tokenType: string;
};

const VaultAreaChart = ({
  chartData,
  markerPoints,
  tokenType,
}: TVaultAreaChartProps) => {
  const showExchangeRate = chartData.showExchangeRate || false;
  const hideLegend = chartData.hideLegend || false;
  const rawChartData = useMemo(
    () => chartData.rawData || [],
    [chartData.rawData]
  );
  const [markerPositions, setMarkerPositions] = useState<
    { id: number; x: number; point: TMarkerPoint }[]
  >([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [hoveredMarker, setHoveredMarker] = useState<TMarkerPoint | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef<Chart<"line"> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipTimeoutRef = useRef<number | null>(null);

  const calculateMarkerPositions = useCallback(() => {
    if (!chartRef.current) return;
    const chart = chartRef.current;
    const positions: { id: number; x: number; point: TMarkerPoint }[] = [];
    const usedPositions: number[] = [];

    markerPoints.forEach((point, idx) => {
      const pointDate = new Date(point.label);
      const formattedPointLabel = pointDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
      const dataIndex = chartData.labels.findIndex(
        (label) => label === formattedPointLabel
      );
      if (dataIndex === -1) return;
      const meta = chart.getDatasetMeta(0);
      if (!meta.data[dataIndex]) return;

      let x = meta.data[dataIndex].x;
      const markerWidth = 24;

      for (const pos of usedPositions) {
        if (Math.abs(x - pos) < markerWidth) {
          x = pos + markerWidth;
        }
      }

      usedPositions.push(x);
      positions.push({ id: idx, x, point });
    });

    setMarkerPositions(positions);
  }, [chartData.labels, markerPoints]);

  // Format date labels based on data range
  const formatDateLabel = (dateStr: string, dataLength: number) => {
    const date = new Date(dateStr);

    // For shorter timeframes (7D, 1M) - show day/month
    if (dataLength <= 31) {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(date);
    }

    // For longer timeframes - show month/year
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "2-digit",
    }).format(date);
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    layout: { padding: { top: 40, left: 0, right: 0, bottom: 20 } },
    animation: {
      onComplete: () => {
        calculateMarkerPositions();
      },
    },
    plugins: {
      tooltip: { enabled: false },
      legend: {
        display: false,
        position: "top",
        align: "end",
        labels: {
          boxWidth: 12,
          padding: 10,
          usePointStyle: true,
          color: COLORS.textPrimary,
          font: {
            size: 12,
            family: "'Inter', sans-serif",
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: dataset.label || `Dataset ${i + 1}`,
              fillStyle: dataset.backgroundColor as string,
              strokeStyle: dataset.borderColor as string,
              pointStyle: "circle",
              lineWidth: 2,
              hidden: !chart.isDatasetVisible(i),
              index: i,
            }));
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, offset: false },
        border: { display: true, width: 1, color: COLORS.primary },
        ticks: {
          font: { size: 10, family: "'Inter', sans-serif" },
          padding: 5,
          color: COLORS.textMuted,
          maxRotation: 0,
          minRotation: 0,
          align: "center",
          autoSkip: true, // Enable automatic skipping for many ticks
          autoSkipPadding: 10, // Padding between ticks when auto-skipping
          maxTicksLimit: 8, // Limit to maximum 8 ticks on x-axis

          callback: function (value, index) {
            const dateStr = chartData.labels[index];
            if (!dateStr) return "";

            return formatDateLabel(dateStr, chartData.labels.length);
          },
        },
        offset: false,
        alignToPixels: true,
        bounds: "ticks",
      },
      y: {
        display: true,
        // min: 0.9,
        ticks: {
          color: COLORS.textMuted,
          font: { size: 10, family: "'Inter', sans-serif" },
          padding: 8,
          autoSkip: true, // Enable automatic skipping for many ticks
          maxTicksLimit: 6, // Limit to maximum 6 ticks on y-axis
        },
        grid: {
          display: false,
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 6,
        hoverBorderColor: COLORS.primaryHoverRGBA,
        hoverBorderWidth: 8,
        backgroundColor: COLORS.primary,
      },
      line: {
        tension: 0.4,
        borderWidth: 2,
        borderColor: COLORS.primary,
      },
    },
    onHover: (event, elements) => {
      if (elements?.length) {
        setHoveredIndex(elements[0].index);
      } else {
        setHoveredIndex(null);
      }
    },
  };

  const data = useMemo(() => {
    // Clone datasets to avoid mutating props
    const datasets = chartData.datasets.map((ds, idx) => {
      // For "Accruing APR" (assumed to be index 2), use Total APR's data for plotting
      if (idx === 2) {
        return {
          ...ds,
          data: chartData.datasets[0].data, // Use Total APR data for plotting
        };
      }
      return ds;
    });

    return {
      labels: chartData.labels,
      datasets,
    };
  }, [chartData.labels, chartData.datasets]);

  // Combine the hover-related effects into one
  useEffect(() => {
    if (!chartRef?.current) return;
    if (hoveredIndex !== null) {
      chartRef.current.update("none");
    }
  }, [hoveredIndex]);

  // Keep the resize effect separate as it's handling a different concern
  useEffect(() => {
    window.addEventListener("resize", calculateMarkerPositions);
    return () => window.removeEventListener("resize", calculateMarkerPositions);
  }, [calculateMarkerPositions]);

  const handleMarkerMouseEnter = (
    e: React.MouseEvent<HTMLDivElement>,
    point: TMarkerPoint
  ) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left - 129 + rect.width / 2,
      y: rect.top - 28 - point.count * 80,
    });
    setHoveredMarker(point);
  };

  const handleMarkerMouseLeave = () => {
    tooltipTimeoutRef.current = window.setTimeout(() => {
      setHoveredMarker(null);
      tooltipTimeoutRef.current = null;
    }, 100);
  };

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [hoveredIndex]);

  useEffect(() => {
    if (!chartRef?.current) return;

    // Only update chart when hoveredIndex changes
    if (hoveredIndex !== null) {
      chartRef.current.update("none");
    }
  }, [hoveredIndex]);

  // Helper to get tooltip data for hoveredIndex
  const tooltipData = useMemo(() => {
    if (
      hoveredIndex !== null &&
      chartData?.datasets?.[0]?.data?.[hoveredIndex] !== undefined
    ) {
      const dateStr = chartData.labels[hoveredIndex];
      const date = new Date(dateStr);
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      // For vaults with exchange rate data, show exchange rate and monthly change
      if (showExchangeRate && rawChartData[hoveredIndex]) {
        const { exchangeRate, monthlyChange } = rawChartData[hoveredIndex];
        return {
          date: formattedDate,
          exchangeRate,
          monthlyChange,
          showExchangeRate: true,
        };
      }

      // For vaults with APR data, show APR breakdown
      const totalApr = chartData.datasets[0].data[hoveredIndex];
      const realisedApr = chartData.datasets[1]?.data[hoveredIndex];
      const accruingApr = chartData.datasets[2]?.data[hoveredIndex];
      return {
        totalApr,
        realisedApr,
        accruingApr,
        date: formattedDate,
        showExchangeRate: false,
      };
    }
    return null;
  }, [
    hoveredIndex,
    chartData.datasets,
    chartData.labels,
    showExchangeRate,
    rawChartData,
  ]);

  const handleChartContainerMouseLeave = () => {
    setHoveredIndex(null);
  };

  useEffect(() => {
    if (!chartRef.current || hideLegend) return;

    const chart = chartRef.current;
    const legendContainer = document.getElementById("vault-legend-container");
    if (!legendContainer) return;

    // Clear previous legend
    legendContainer.innerHTML = "";

    // Create legend items
    const legendItems =
      chart.options.plugins?.legend?.labels?.generateLabels?.(chart) || [];

    legendItems.forEach((item) => {
      const legendItem = document.createElement("div");
      legendItem.style.display = "flex";
      legendItem.style.alignItems = "center";
      legendItem.style.marginBottom = "8px";

      const colorBox = document.createElement("div");
      if (item.text === "Total APR") {
        // Render a horizontal line for Total APR
        colorBox.style.width = "18px";
        colorBox.style.height = "3px";
        colorBox.style.backgroundColor = item.strokeStyle as string;
        colorBox.style.borderRadius = "2px";
        colorBox.style.marginRight = "8px";
        colorBox.style.marginTop = "1px";
      } else {
        // Render a circle for others
        colorBox.style.width = "12px";
        colorBox.style.height = "12px";
        colorBox.style.borderRadius = "50%";
        colorBox.style.backgroundColor = item.fillStyle as string;
        colorBox.style.borderBlock = item.strokeStyle as string;
        colorBox.style.marginRight = "8px";
        colorBox.style.border =
          item.text === "Total APR" ? `2px solid ${item.strokeStyle}` : "none";
      }
      colorBox.style.opacity = item.hidden ? "0.5" : "1";

      const label = document.createElement("span");
      label.textContent = item.text;
      label.style.fontSize = "12px";
      label.style.fontFamily = "'Inter', sans-serif";
      label.style.color = COLORS.textMuted;
      label.style.textDecoration = item.hidden ? "line-through" : "none";
      label.style.opacity = item.hidden ? "0.5" : "1";

      legendItem.appendChild(colorBox);
      legendItem.appendChild(label);

      legendContainer.appendChild(legendItem);
    });
  }, [chartData, hideLegend]);

  return (
    <div className={styles["dashboard-container"]}>
      <div className={styles["chart-card"]}>
        <div
          className={styles["chart-area"]}
          ref={containerRef}
          onMouseLeave={handleChartContainerMouseLeave}
        >
          <Line ref={chartRef} data={data} options={options as any} />

          {hoveredIndex !== null && tooltipData && chartRef.current && (
            <div
              className={styles["custom-tooltip"]}
              style={{
                pointerEvents: "none",
                left: Math.max(
                  10,
                  Math.min(
                    chartRef.current.getDatasetMeta(showExchangeRate ? 0 : 1)
                      .data[hoveredIndex].x - 90,
                    containerRef.current!.offsetWidth - 210
                  )
                ),
                top:
                  chartRef.current.getDatasetMeta(showExchangeRate ? 0 : 1)
                    .data[hoveredIndex].y + 30,
                position: "absolute",
                zIndex: 12,
              }}
              onMouseLeave={() => {
                if (tooltipTimeoutRef.current) {
                  clearTimeout(tooltipTimeoutRef.current);
                }
                tooltipTimeoutRef.current = window.setTimeout(() => {
                  setHoveredIndex(null);
                }, 0);
              }}
            >
              <div
                style={{
                  background: COLORS.white,
                  borderRadius: "12px",
                  boxShadow: COLORS.shadow,
                  padding: "14px",
                  minWidth: "200px",
                  border: `1px solid ${COLORS.borderLight}`,
                }}
              >
                {tooltipData.date && (
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: 12,
                      marginBottom: 8,
                      color: COLORS.textSecondary,
                    }}
                  >
                    {tooltipData.date}
                  </div>
                )}
                <hr
                  style={{
                    border: "none",
                    borderTop: `1px solid ${COLORS.divider}`,
                    margin: "8px 0",
                  }}
                />
                {tooltipData.showExchangeRate ? (
                  // Exchange rate tooltip - show exchange rate and monthly change
                  <>
                    <div
                      style={{
                        fontWeight: 500,
                        fontSize: 10,
                        marginBottom: 8,
                        color: COLORS.textPrimary,
                      }}
                    >
                      Exchange Rate
                      <span
                        style={{
                          float: "right",
                          fontWeight: 400,
                          fontSize: 10,
                        }}
                      >
                        &nbsp;1 {tokenType} ={" "}
                        {tooltipData?.exchangeRate?.toFixed(4)}{" "}
                        {tokenType.toLowerCase() === "sbusd" ? "USD" : "ETH"}
                      </span>
                    </div>
                    {/* {tooltipData.monthlyChange !== undefined && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          color: COLORS.textSecondary,
                        }}
                      >
                        <span>Monthly Change</span>
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              tooltipData.monthlyChange >= 0
                                ? "#00B7A8"
                                : "#FF6B6B",
                          }}
                        >
                          {tooltipData.monthlyChange >= 0 ? "+" : ""}
                          {tooltipData.monthlyChange.toFixed(2)}%
                        </span>
                      </div>
                    )} */}
                  </>
                ) : (
                  // Other vaults tooltip - show APR breakdown
                  <>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        marginBottom: 1,
                        color: COLORS.textPrimary,
                      }}
                    >
                      Total APR{" "}
                      <span style={{ float: "right", fontWeight: 700 }}>
                        {tooltipData?.totalApr?.toFixed(2)}%
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: COLORS.textSecondary,
                        marginBottom: 1,
                      }}
                    >
                      <span>Realised APR</span>
                      <span
                        style={{ fontWeight: 600, color: COLORS.textPrimary }}
                      >
                        {tooltipData?.realisedApr?.toFixed(2)}%
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: COLORS.textSecondary,
                      }}
                    >
                      <span>Accruing APR</span>
                      <span
                        style={{ fontWeight: 600, color: COLORS.textPrimary }}
                      >
                        {tooltipData?.accruingApr?.toFixed(2)}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {markerPositions.map(({ id, x, point }) => (
            <div
              key={id}
              className={styles["chart-marker"]}
              style={{
                position: "absolute",
                bottom: "-15px",
                left: `${x - 18}px`,
              }}
              onMouseEnter={(e) => handleMarkerMouseEnter(e, point)}
              onMouseLeave={handleMarkerMouseLeave}
            >
              <span>{point.count}</span>
            </div>
          ))}

          {hoveredMarker && (
            <div
              className={styles["info-card"]}
              style={{
                top: tooltipPosition.y + "px",
                left: tooltipPosition.x + "px",
              }}
              onMouseEnter={() => {
                if (tooltipTimeoutRef.current)
                  clearTimeout(tooltipTimeoutRef.current);
              }}
              onMouseLeave={() => {
                tooltipTimeoutRef.current = window.setTimeout(() => {
                  setHoveredMarker(null);
                  tooltipTimeoutRef.current = null;
                }, 100);
              }}
            >
              {hoveredMarker.data.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom:
                      idx < hoveredMarker.data.length - 1 ? "16px" : "0",
                  }}
                >
                  <div className={styles["info-card-title"]}>
                    <span role="img" aria-label="flag">
                      <FiFlag
                        className={styles["marker-icon"]}
                        color={COLORS.primary}
                        style={{ marginTop: "4px" }}
                      />
                    </span>
                    <div className={styles["info-card-content"]}>
                      <p>
                        {item.event}
                        <span style={{ marginLeft: "4px" }}>
                          {item.ctaLink && item.ctaText ? (
                            <a
                              href={item.ctaLink}
                              target="_blank"
                              rel="noreferrer"
                              className="inline underline font-bold text-[#1A1A1A]"
                              style={{
                                textDecoration: "underline",
                                fontWeight: "bold",
                              }}
                            >
                              {item.ctaText}
                            </a>
                          ) : null}
                        </span>
                      </p>

                      <div className={styles["info-card-date"]}>
                        {item.timestamp}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VaultAreaChart;
